// Production data layer (Module 04 §5, slice 04-A). TanStack Query hooks over
// the generated api-client — doctrine 3: apps/web talks to the API only through
// `@assessly/api-client`, never a hand-rolled fetch. Query keys live in
// `productionKeys`; every mutation sends a fresh `Idempotency-Key` (§5: "all
// mutations require Idempotency-Key") and invalidates the lists it affects.
// Errors are normalised by `unwrap` into `RequestError` (request id + problem
// `code` for the per-code messages in production.i18n.ts) / `UnauthorizedError`.
//
// REDACTION IS KEY ABSENCE HERE, NOT NULL (spec 04 §7 — a deliberate divergence
// from module 06's null+`redacted:true`). Every `*_minor` field and the whole
// `costs` key are `.optional()` in the contract: present with a value for a
// caller holding `production.view_costs`, ABSENT otherwise. Screens must branch
// on `key !== undefined` / `"costs" in detail` — never render undefined as 0,
// and never draw a column whose keys are absent.
//
// STALENESS AFTER A RESULT (§3.11): the first recorded result flips a WO
// released→in_progress, and `auto_complete_wo` may complete it in the same
// call — so every operation-level mutation invalidates the WO DETAIL and the
// LIST, and every priority write invalidates the station queues (priority is
// WO-level and re-orders every column that carries one of its operations).

import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import type { components, paths } from "@assessly/api-client";
import { client } from "../../../../lib/api";
import { refreshQuery } from "../../../../lib/query";
import { newIdempotencyKey, RequestError, unwrap } from "../../../../lib/request";

type S = components["schemas"];

export type WorkOrder = S["WorkOrder"];
export type WorkOrderOperation = S["WorkOrderOperation"];
export type WorkOrderMaterialLine = S["WorkOrderMaterialLine"];
export type WorkOrderDetail = S["WorkOrderDetailResponse"];
export type WorkOrderShortage = S["WorkOrderShortage"];
export type WoOutput = S["WoOutput"];
export type WoOutputReceipt = S["WoOutputReceipt"];
export type WoCostEntry = S["WoCostEntry"];
export type WorkOrderCostSummary = S["WorkOrderCostSummary"];
export type MaterialIssue = S["MaterialIssue"];
export type OperationQtyReport = S["OperationQtyReport"];
/** §2.14 (04-C-3) — the S2 Genealogy tab's row, served on e3. */
export type SerialLotCapture = S["SerialLotCapture"];
/** §2.14's two directions. Only `produced` satisfies the §3.2 completion guard. */
export type SerialLotDirection = S["SerialLotDirection"];
/** One entry of e34's `serial_captures[]` — the OFFICE half of a §2.14 capture
 * (§3.11's paper traveler, D-085 pt 3). Named off the request type rather than
 * off the schema alias so the form can never post a shape the endpoint would
 * not accept. */
export type OperationResultSerialCapture = S["OperationResultSerialCapture"];
export type StationQueueOperation = S["StationQueueOperation"];
export type StationQueue = S["StationQueueResponse"];
export type WorkOrderCreateRequest = S["WorkOrderCreateRequest"];
export type WorkOrderPatchRequest = S["WorkOrderPatchRequest"];
export type WorkOrderCancelRequest = S["WorkOrderCancelRequest"];
export type OperationResultRequest = S["OperationResultRequest"];
export type MaterialIssueCreateRequest = S["MaterialIssueCreateRequest"];
export type WoCostAdjustmentRequest = S["WoCostAdjustmentRequest"];
export type WorkOrderStatus = WorkOrder["status"];
export type OperationStatus = WorkOrderOperation["status"];

/** The sales order a PEGGED create is for (D-290) — module 09's own §4.7 read,
 * `GET /v1/orders/{id}`, through the same generated client as everything here
 * (doctrine 3: the web talks to module 09 through its API, never around it). */
export type PegOrder = S["OrderDetailResponse"];
export type PegOrderLine = PegOrder["lines"][number];

/** One §2.3 `operation_events` row as §5 e10 serves it (04-B-2 wire; the S2
 * Events tab is its first office screen). `payload` is the §5.1 wire verbatim
 * — quantities as decimal strings, never JSON numbers (D-041). */
export type OperationEvent = S["OperationEvent"];
export type OperationEventType = OperationEvent["event_type"];

export type FloorSyncConflict = S["FloorSyncConflict"];
export type FloorConflictResolveRequest = S["FloorConflictResolveRequest"];
export type FloorDevice = S["FloorDevice"];
export type FloorDeviceCreateRequest = S["FloorDeviceCreateRequest"];
export type FloorDeviceUpdateRequest = S["FloorDeviceUpdateRequest"];

export type WorkOrdersQuery = NonNullable<paths["/v1/work-orders"]["get"]["parameters"]["query"]>;
type CostsQuery = NonNullable<paths["/v1/work-orders/{id}/costs"]["get"]["parameters"]["query"]>;
type ProductsQuery = NonNullable<paths["/v1/catalogue/products"]["get"]["parameters"]["query"]>;
export type FloorConflictsQuery = NonNullable<
  paths["/v1/floor/conflicts"]["get"]["parameters"]["query"]
>;
export type FloorDevicesQuery = NonNullable<
  paths["/v1/floor/devices"]["get"]["parameters"]["query"]
>;
export type WorkOrderEventsQuery = NonNullable<
  paths["/v1/work-orders/{id}/events"]["get"]["parameters"]["query"]
>;

// ---- query keys -------------------------------------------------------------

export const productionKeys = {
  all: ["production"] as const,
  workOrders: (query: WorkOrdersQuery = {}) => ["production", "work-orders", query] as const,
  workOrder: (id: string) => ["production", "work-order", id] as const,
  costs: (id: string, cursor: string | undefined) =>
    ["production", "work-order", id, "costs", cursor ?? null] as const,
  /** Shares the `["production", "work-order", id]` prefix DELIBERATELY: every
   * operation-level mutation invalidates `productionKeys.workOrder(id)`, and a
   * §3.11 office result must refresh this timeline exactly as it refreshes the
   * detail it rode in on. */
  events: (id: string, query: WorkOrderEventsQuery) =>
    ["production", "work-order", id, "events", query] as const,
  /** ALL station queues share the `["production", "station-queue"]` prefix so a
   * WO-level priority write can invalidate every column in one call — the
   * D-072 rule: the SERVER re-orders, the client only re-fetches. */
  stationQueue: (workCenterId: string) => ["production", "station-queue", workCenterId] as const,
  facilities: () => ["production", "ref", "facilities"] as const,
  departments: (facilityId: string) =>
    ["production", "ref", "facility", facilityId, "departments"] as const,
  workCenters: (facilityId: string | undefined, status?: string) =>
    ["production", "ref", "work-centers", facilityId ?? null, status ?? null] as const,
  product: (id: string) => ["production", "ref", "product", id] as const,
  productSearch: (query: ProductsQuery) => ["production", "ref", "products", query] as const,
  productBoms: (productId: string) => ["production", "ref", "product", productId, "boms"] as const,
  productRoutings: (productId: string) =>
    ["production", "ref", "product", productId, "routings"] as const,
  effectiveBom: (productId: string, facilityId: string) =>
    ["production", "ref", "product", productId, "effective-bom", facilityId] as const,
  effectiveRouting: (productId: string, facilityId: string) =>
    ["production", "ref", "product", productId, "effective-routing", facilityId] as const,
  bomLines: (bomId: string) => ["production", "ref", "bom", bomId, "lines"] as const,
  uoms: () => ["production", "ref", "uoms"] as const,
  atp: (productId: string, facilityId: string) =>
    ["production", "ref", "atp", productId, facilityId] as const,
  scrapReasons: () => ["production", "ref", "scrap-reasons"] as const,
  downtimeReasons: () => ["production", "ref", "downtime-reasons"] as const,
  /** S13's e22 probe. NOT under the `ref` prefix — a reference list is cached
   * for five minutes and this one is the live fact the board is about. */
  downtimeOpen: (facilityId: string | undefined) =>
    ["production", "downtime", "open", facilityId ?? null] as const,
  conflicts: (query: FloorConflictsQuery = {}) => ["production", "conflicts", query] as const,
  /** The nav badge's count probe shares the conflicts prefix so a resolve
   * invalidates both in one call. */
  conflictBadge: () => ["production", "conflicts", "badge"] as const,
  devices: (query: FloorDevicesQuery = {}) => ["production", "devices", query] as const,
  /** The sales order a pegged create reads for its banner, locked product and
   * prefilled quantity (D-290). Under the production prefix: this is the
   * modal's read, cached with the modal's lifetime, not the sales screen's. */
  pegOrder: (orderId: string) => ["production", "peg-order", orderId] as const,
} as const;

const WORK_ORDERS = ["production", "work-orders"] as const;
const STATION_QUEUES = ["production", "station-queue"] as const;

/**
 * THE SALES READS A PEGGED WORK ORDER MOVES (D-290). Module 04's create,
 * release, first result, completion, close and cancel each call module 09's
 * peg port in their own transaction: a new chip, a line into or out of
 * `in_production`, a confirmed order into `in_production`. These are the sales
 * area's own key prefixes (`quoteKeys.order` / `quoteKeys.orderLists` in
 * areas/office/sales/hooks/quote-queries.ts) — invalidated here so an order
 * detail or order list already in the cache is re-read rather than shown as it
 * was before the work order moved. An unpegged work order moves nothing there,
 * and invalidating an unmounted query only marks it stale, so every WO write
 * that can reach the port invalidates without asking whether it was pegged.
 */
const SALES_ORDER_DETAILS = ["sales", "order"] as const;
const SALES_ORDER_LISTS = ["sales", "orders"] as const;

function invalidatePeggedSalesReads(qc: ReturnType<typeof useQueryClient>): void {
  void qc.invalidateQueries({ queryKey: SALES_ORDER_DETAILS });
  void qc.invalidateQueries({ queryKey: SALES_ORDER_LISTS });
}

function idempotentHeader() {
  return { "Idempotency-Key": newIdempotencyKey() } as const;
}

/** Reference data barely changes within a session; one fetch, cached. */
const REF_OPTIONS = { staleTime: 5 * 60_000, gcTime: 30 * 60_000, retry: false } as const;

// =============================================================================
// §4 S1 / S2 — work orders
// =============================================================================

/**
 * §5 row 2. `enabled: false` holds the read back: S1 passes the saved-views
 * controller's `listEnabled`, so an address naming a view alone is read once,
 * with the view applied, and never first as the bare list (D-305 ruling 21).
 */
export function useWorkOrders(query: WorkOrdersQuery = {}, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: productionKeys.workOrders(query),
    enabled: options.enabled ?? true,
    retry: false,
    queryFn: async ({ signal }) =>
      unwrap(await client.GET("/v1/work-orders", { signal, params: { query } }), "work-orders"),
  });
}

export function useWorkOrder(id: string) {
  return useQuery({
    queryKey: productionKeys.workOrder(id),
    retry: false,
    enabled: id.length > 0,
    queryFn: async ({ signal }) =>
      unwrap(
        await client.GET("/v1/work-orders/{id}", { signal, params: { path: { id } } }),
        "work-order",
      ),
  });
}

/**
 * The Costs tab's ledger (§5 row 11). `enabled` MUST be driven by the detail
 * payload's `costs` key being present (key-absence redaction, §7) — the tab is
 * only offered then, so this query is never fired just to collect a 403.
 */
export function useWorkOrderCosts(id: string, cursor: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: productionKeys.costs(id, cursor),
    retry: false,
    enabled: enabled && id.length > 0,
    queryFn: async ({ signal }) => {
      const query: CostsQuery = cursor !== undefined ? { cursor } : {};
      return unwrap(
        await client.GET("/v1/work-orders/{id}/costs", { signal, params: { path: { id }, query } }),
        "work-order-costs",
      );
    },
  });
}

/**
 * §5 e10 — the WO event timeline (the S2 Events tab). Keyset cursor over
 * `occurred_at` (the PHYSICAL timeline, §2.3's index), `event_type[]` filter
 * per the §5 row. Fenced by `production.read` — e10's own gate — which the tab
 * mirrors client-side before this query ever fires. The page is accumulated
 * behind "Load more" by the caller (the costs-ledger pattern): the truncation
 * class has bitten six times, so a single-page read is never treated as the
 * whole timeline — `next_cursor` decides, not row count.
 */
export function useWorkOrderEvents(id: string, query: WorkOrderEventsQuery) {
  return useQuery({
    queryKey: productionKeys.events(id, query),
    retry: false,
    enabled: id.length > 0,
    queryFn: async ({ signal }) =>
      unwrap(
        await client.GET("/v1/work-orders/{id}/events", {
          signal,
          params: { path: { id }, query },
        }),
        "work-order-events",
      ),
  });
}

/**
 * The sales order a PEGGED create is for (§4 S3 pre-pegged, D-290): the
 * banner's "SO-… · line N", the line's product (locked) and its open quantity
 * (prefilled). `GET /v1/orders/{id}` answers `sales.read`, which every holder of
 * `production.wo.write` also holds (group_admin, facility_admin, planner). Read
 * only while the modal is pegged — an unpegged create never calls it.
 */
export function usePegOrder(orderId: string) {
  return useQuery({
    queryKey: productionKeys.pegOrder(orderId),
    retry: false,
    enabled: orderId.length > 0,
    queryFn: async ({ signal }) =>
      unwrap(
        await client.GET("/v1/orders/{id}", { signal, params: { path: { id: orderId } } }),
        "peg-order",
      ),
  });
}

export function useCreateWorkOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: WorkOrderCreateRequest) =>
      unwrap(
        await client.POST("/v1/work-orders", { params: { header: idempotentHeader() }, body }),
        "create-work-order",
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: WORK_ORDERS });
      invalidatePeggedSalesReads(qc);
    },
  });
}

/**
 * PATCH (§5 row 8) — the draft editor AND the dispatch board's priority write.
 * Priority is WO-LEVEL, so a drag on one column re-orders every column holding
 * an operation of that WO: all station queues are invalidated, and the board
 * re-renders the SERVED order (D-072) — the card may legally not land where it
 * was dropped.
 *
 * The station queues are REFRESHED (cancel-then-invalidate, lib/query.ts), not
 * merely invalidated. The board mounts one `useStationQueue` per column at
 * once, and a drag only proves the SOURCE column has loaded: a slower column is
 * routinely still on its FIRST fetch when the drop lands, and a plain
 * invalidate coalesces into that pre-drop read instead of refiring. That column
 * then renders the pre-drop order — silently wrong, since the board has no
 * polling and never re-sorts client-side — until the operator navigates away.
 * Every other STATION_QUEUES invalidator fires from a screen where no queue is
 * mounted, so they stay plain.
 */
export function usePatchWorkOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; body: WorkOrderPatchRequest }) =>
      unwrap(
        await client.PATCH("/v1/work-orders/{id}", {
          params: { path: { id: input.id }, header: idempotentHeader() },
          body: input.body,
        }),
        "patch-work-order",
      ),
    onSuccess: (_data, input) => {
      void qc.invalidateQueries({ queryKey: WORK_ORDERS });
      void qc.invalidateQueries({ queryKey: productionKeys.workOrder(input.id) });
      void refreshQuery(qc, STATION_QUEUES);
    },
  });
}

/**
 * Release (§5 row 4 / §4 S3 step 2). Callers post WITHOUT `acknowledge_shortages`
 * first; a 409 `WO_RELEASE_SHORTAGES_UNACKNOWLEDGED` means "confirm and repost
 * `{acknowledge_shortages: true}`" — the modal owns that confirmation. The
 * 200's `shortages[]` is the authoritative post-release display; any client-side
 * ATP preview is advisory only.
 */
export function useReleaseWorkOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; acknowledgeShortages?: boolean }) =>
      unwrap(
        await client.POST("/v1/work-orders/{id}/release", {
          params: { path: { id: input.id }, header: idempotentHeader() },
          body: input.acknowledgeShortages === true ? { acknowledge_shortages: true } : {},
        }),
        "release-work-order",
      ),
    onSuccess: (_data, input) => {
      void qc.invalidateQueries({ queryKey: WORK_ORDERS });
      void qc.invalidateQueries({ queryKey: productionKeys.workOrder(input.id) });
      void qc.invalidateQueries({ queryKey: STATION_QUEUES });
      invalidatePeggedSalesReads(qc);
    },
  });
}

export function useCompleteWorkOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      unwrap(
        await client.POST("/v1/work-orders/{id}/complete", {
          params: { path: { id }, header: idempotentHeader() },
        }),
        "complete-work-order",
      ),
    onSuccess: (_data, id) => {
      void qc.invalidateQueries({ queryKey: WORK_ORDERS });
      void qc.invalidateQueries({ queryKey: productionKeys.workOrder(id) });
      void qc.invalidateQueries({ queryKey: STATION_QUEUES });
      invalidatePeggedSalesReads(qc);
    },
  });
}

export function useCloseWorkOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      unwrap(
        await client.POST("/v1/work-orders/{id}/close", {
          params: { path: { id }, header: idempotentHeader() },
        }),
        "close-work-order",
      ),
    onSuccess: (_data, id) => {
      void qc.invalidateQueries({ queryKey: WORK_ORDERS });
      void qc.invalidateQueries({ queryKey: productionKeys.workOrder(id) });
      invalidatePeggedSalesReads(qc);
    },
  });
}

export function useCancelWorkOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; body: WorkOrderCancelRequest }) =>
      unwrap(
        await client.POST("/v1/work-orders/{id}/cancel", {
          params: { path: { id: input.id }, header: idempotentHeader() },
          body: input.body,
        }),
        "cancel-work-order",
      ),
    onSuccess: (_data, input) => {
      void qc.invalidateQueries({ queryKey: WORK_ORDERS });
      void qc.invalidateQueries({ queryKey: productionKeys.workOrder(input.id) });
      void qc.invalidateQueries({ queryKey: STATION_QUEUES });
      invalidatePeggedSalesReads(qc);
    },
  });
}

// =============================================================================
// §4 S2 — operations (skip + the §3.11 office-recorded result)
// =============================================================================

export function useSkipOperation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { workOrderId: string; opId: string; reason: string }) =>
      unwrap(
        await client.POST("/v1/work-orders/{id}/operations/{opId}/skip", {
          params: {
            path: { id: input.workOrderId, opId: input.opId },
            header: idempotentHeader(),
          },
          body: { reason: input.reason },
        }),
        "skip-operation",
      ),
    onSuccess: (_data, input) => {
      void qc.invalidateQueries({ queryKey: productionKeys.workOrder(input.workOrderId) });
      void qc.invalidateQueries({ queryKey: WORK_ORDERS });
      void qc.invalidateQueries({ queryKey: STATION_QUEUES });
    },
  });
}

/**
 * The office-recorded result (§5 row 34 / §3.11 — the paper-traveler path).
 * After success the caller MUST work from the re-fetched detail, never the
 * pre-mutation status: the first result flips released→in_progress, and
 * `auto_complete_wo` may have completed the WO in the same transaction.
 */
export function useRecordOperationResult() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      workOrderId: string;
      opId: string;
      body: OperationResultRequest;
    }) =>
      unwrap(
        await client.POST("/v1/work-orders/{id}/operations/{opId}/result", {
          params: {
            path: { id: input.workOrderId, opId: input.opId },
            header: idempotentHeader(),
          },
          body: input.body,
        }),
        "record-operation-result",
      ),
    onSuccess: (_data, input) => {
      void qc.invalidateQueries({ queryKey: productionKeys.workOrder(input.workOrderId) });
      void qc.invalidateQueries({ queryKey: WORK_ORDERS });
      void qc.invalidateQueries({ queryKey: STATION_QUEUES });
      void qc.invalidateQueries({
        queryKey: ["production", "work-order", input.workOrderId, "costs"],
      });
      // The first result flips a pegged work order's chip to in progress.
      invalidatePeggedSalesReads(qc);
    },
  });
}

// =============================================================================
// §4 S2 Materials — office-side manual issue AND return (§5 row 30)
// =============================================================================

/**
 * Both branches of §5 row 30 since 04-B-4 (D-076/D-081): a positive `qty`
 * issues; a negative `qty` returns, naming its `reversal_of_issue_id` in the
 * original's UoM — the server posts module 06's `material_return` movement in
 * the same transaction and the Materials tab's Return action composes it.
 *
 * A return on a COMPLETED order is the same call (D-308): e30 takes it until
 * the order is closed, and its success refreshes the detail's Materials plan
 * and the Costs tab's ledger exactly as a live order's return does, because
 * the costs key shares the detail's prefix.
 *
 * A refusal that means the ORDER moved under the reader — 409
 * `WO_CLOSED_IMMUTABLE` (finance closed it) or `WO_INVALID_TRANSITION` (it was
 * cancelled, or completed under an issue form) — refreshes the detail too, so
 * the status chip and the controls follow the order at once instead of
 * offering a control that can only be refused again.
 *
 * THE KEY RIDES THE INTENT, NOT THE CALL (the D-308 review, `useEndDowntime`'s
 * rule). The form mints the `Idempotency-Key` with `useIntentKey`
 * (hooks/intent-key.ts) and passes it here, so a return whose response was
 * lost replays its first 201 when pressed again instead of booking a second
 * return. A key minted per call did exactly that: phantom bulk on the bin and a
 * doubled material credit, which the close then freezes into `cost_breakdown`.
 */
export function useCreateMaterialIssue() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      workOrderId: string;
      body: MaterialIssueCreateRequest;
      idempotencyKey: string;
    }) =>
      unwrap(
        await client.POST("/v1/work-orders/{id}/material-issues", {
          params: {
            path: { id: input.workOrderId },
            header: { "Idempotency-Key": input.idempotencyKey },
          },
          body: input.body,
        }),
        "material-issue",
      ),
    onSuccess: (_data, input) => {
      void qc.invalidateQueries({ queryKey: productionKeys.workOrder(input.workOrderId) });
      void qc.invalidateQueries({
        queryKey: ["production", "work-order", input.workOrderId, "costs"],
      });
    },
    onError: (error, input) => {
      if (
        error instanceof RequestError &&
        (error.code === "WO_CLOSED_IMMUTABLE" || error.code === "WO_INVALID_TRANSITION")
      ) {
        void qc.invalidateQueries({ queryKey: productionKeys.workOrder(input.workOrderId) });
        void qc.invalidateQueries({ queryKey: WORK_ORDERS });
      }
    },
  });
}

// =============================================================================
// §4 S2 Costs — manual adjustment (§5 row 12)
// =============================================================================

export function useCreateCostAdjustment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { workOrderId: string; body: WoCostAdjustmentRequest }) =>
      unwrap(
        await client.POST("/v1/work-orders/{id}/costs/adjustments", {
          params: { path: { id: input.workOrderId }, header: idempotentHeader() },
          body: input.body,
        }),
        "cost-adjustment",
      ),
    onSuccess: (_data, input) => {
      void qc.invalidateQueries({ queryKey: productionKeys.workOrder(input.workOrderId) });
      void qc.invalidateQueries({
        queryKey: ["production", "work-order", input.workOrderId, "costs"],
      });
    },
  });
}

// =============================================================================
// §4 S2 — traveler PDF (§5 row 29)
// =============================================================================

/**
 * The traveler is a BLOB, not JSON (the catalogue `qr.pdf` pattern): the
 * mutation returns the Blob and the screen opens/saves it. A 409 on a draft
 * (`WO_INVALID_TRANSITION` — a draft has no traveler) surfaces as a
 * RequestError like any other refusal.
 */
export function useTravelerDownload() {
  return useMutation({
    mutationFn: async (id: string) => {
      const result = await client.GET("/v1/work-orders/{id}/traveler", {
        params: { path: { id } },
        parseAs: "blob",
      });
      return unwrap(result as { data?: Blob; error?: unknown; response: Response }, "traveler");
    },
  });
}

// =============================================================================
// §4 S4 — station queue (§5 row 14, D-072)
// =============================================================================

/**
 * One column of the dispatch board. THE D-072 RULE: the served array IS the
 * order — `priority ASC, scheduled_start_at ASC NULLS LAST, due_date ASC NULLS
 * LAST, wo_no ASC, seq_no ASC` is computed by the SERVER's comparator
 * (packages/domain-kernel/production/queue-order.ts) and the screen renders
 * `operations[]` in ARRAY ORDER, never re-sorts. `delta_cursor` exists for the
 * 04-B offline cache; the board ignores it and refetches on invalidation.
 */
export function useStationQueue(workCenterId: string, enabled = true) {
  return useQuery({
    queryKey: productionKeys.stationQueue(workCenterId),
    retry: false,
    enabled: enabled && workCenterId.length > 0,
    queryFn: async ({ signal }) =>
      unwrap(
        await client.GET("/v1/floor/stations/{workCenterId}/queue", {
          signal,
          params: { path: { workCenterId } },
        }),
        "station-queue",
      ),
  });
}

// =============================================================================
// Reference data — cross-module READS through the same generated client.
// =============================================================================

/**
 * `enabled` defaults to true so every existing office call site is unchanged.
 * The floor lookup passes false once its tablet is provisioned: a facility
 * picker that has already been answered must not keep the org tree on a poll
 * from a shopfloor device.
 */
export function useFacilities(enabled = true) {
  return useQuery({
    ...REF_OPTIONS,
    enabled,
    queryKey: productionKeys.facilities(),
    queryFn: async ({ signal }) =>
      unwrap(
        await client.GET("/v1/org/facilities", { signal, params: { query: { status: "active" } } }),
        "facilities",
      ),
  });
}

export function useDepartments(facilityId: string) {
  return useQuery({
    ...REF_OPTIONS,
    queryKey: productionKeys.departments(facilityId),
    enabled: facilityId.length > 0,
    queryFn: async ({ signal }) =>
      unwrap(
        await client.GET("/v1/org/facilities/{id}/departments", {
          signal,
          params: { path: { id: facilityId } },
        }),
        "departments",
      ),
  });
}

/**
 * Work centres of a facility (S4's columns). The department cut is CLIENT-side
 * (`department_id` on the row): §5 has no department filter on this list.
 *
 * `status` is served by the endpoint (module 16 `/v1/org/work-centers`), and a
 * PICKER must pass "active": an archived station is by definition one nobody may
 * be sent to, and offering it is a control that exists to be refused. Callers
 * that LABEL an existing row (a work order's station, an operation's snapshot)
 * deliberately leave it unset — a work order authored before a station was
 * archived still has to render its name — and hand any picker on the same
 * screen `pickableWorkCenters` (lib/work-centers.ts).
 */
/**
 * EVERY work centre the caller can see, unfiltered.
 *
 * The station QR (`/q/wc/{id}`) carries an id and nothing else, so a tablet
 * that arrives by scan — the PRODUCTION path, §3.3 step 2's context anchor —
 * knows which machine it is bolted to only as a uuid, and `StationSnapshot`
 * carries no station identity at all (devices, shift, queue, and no name). The
 * station screen then titles itself "Station", and an operator has no
 * confirmation of which machine their quantities are being booked to.
 *
 * Its own key, NOT `workCenters(undefined)`: that key belongs to the picker's
 * "no facility chosen yet" state, which is disabled on purpose, and filling it
 * here would make the picker briefly list every facility's work centres.
 */
export function useAllWorkCenters(enabled: boolean) {
  return useQuery({
    ...REF_OPTIONS,
    queryKey: [...productionKeys.all, "work-centers-all"] as const,
    enabled,
    queryFn: async ({ signal }) =>
      unwrap(
        await client.GET("/v1/org/work-centers", { signal, params: { query: {} } }),
        "work-centers",
      ),
  });
}

export function useWorkCenters(facilityId: string | undefined, status?: "active" | "archived") {
  return useQuery({
    ...REF_OPTIONS,
    queryKey: productionKeys.workCenters(facilityId, status),
    enabled: facilityId !== undefined && facilityId.length > 0,
    queryFn: async ({ signal }) =>
      unwrap(
        await client.GET("/v1/org/work-centers", {
          signal,
          params: {
            query: {
              ...(facilityId !== undefined ? { facility_id: facilityId } : {}),
              ...(status !== undefined ? { status } : {}),
            },
          },
        }),
        "work-centers",
      ),
  });
}

export interface ProductRef {
  readonly id: string;
  readonly sku: string;
  readonly name: string;
  readonly base_uom_id: string;
}

/**
 * Resolve a SET of product ids to `{sku, name}` for a page of work orders — the
 * inventory house pattern (inventory-queries.ts `useProductIndex`): one cached
 * query per product, never a whole-catalogue fetch. The station queue does NOT
 * need this — `product_name` is served on the card (§5 row 14).
 */
export function useProductIndex(ids: readonly string[]) {
  const unique = Array.from(new Set(ids.filter((id) => id.length > 0))).sort();
  return useQueries({
    queries: unique.map((id) => ({
      ...REF_OPTIONS,
      queryKey: productionKeys.product(id),
      queryFn: async ({ signal }) =>
        unwrap(
          await client.GET("/v1/catalogue/products/{id}", { signal, params: { path: { id } } }),
          "product",
        ),
    })),
    combine: (results) => {
      const byId = new Map<string, ProductRef>();
      for (const result of results) {
        const product = result.data;
        if (product !== undefined) {
          byId.set(product.id, {
            id: product.id,
            sku: product.sku,
            name: product.name,
            base_uom_id: product.base_uom_id,
          });
        }
      }
      return { byId, isPending: results.some((r) => r.isPending) };
    },
  });
}

/** S3's product picker (`catalogue.read`, held by every office role). */
export function useProductSearch(q: string) {
  const query: ProductsQuery = q.length > 0 ? { q } : {};
  return useQuery({
    ...REF_OPTIONS,
    queryKey: productionKeys.productSearch(query),
    queryFn: async ({ signal }) =>
      unwrap(await client.GET("/v1/catalogue/products", { signal, params: { query } }), "products"),
  });
}

export function useProductBoms(productId: string) {
  return useQuery({
    ...REF_OPTIONS,
    queryKey: productionKeys.productBoms(productId),
    enabled: productId.length > 0,
    queryFn: async ({ signal }) =>
      unwrap(
        await client.GET("/v1/catalogue/products/{id}/boms", {
          signal,
          params: { path: { id: productId } },
        }),
        "boms",
      ),
  });
}

export function useProductRoutings(productId: string) {
  return useQuery({
    ...REF_OPTIONS,
    queryKey: productionKeys.productRoutings(productId),
    enabled: productId.length > 0,
    queryFn: async ({ signal }) =>
      unwrap(
        await client.GET("/v1/catalogue/products/{id}/routings", {
          signal,
          params: { path: { id: productId } },
        }),
        "routings",
      ),
  });
}

/** The auto-pinned version S3 shows on product pick (spec 01 §3.4). */
export function useEffectiveBom(productId: string, facilityId: string) {
  return useQuery({
    ...REF_OPTIONS,
    queryKey: productionKeys.effectiveBom(productId, facilityId),
    enabled: productId.length > 0 && facilityId.length > 0,
    queryFn: async ({ signal }) =>
      unwrap(
        await client.GET("/v1/catalogue/products/{id}/boms/effective", {
          signal,
          params: { path: { id: productId }, query: { facility_id: facilityId } },
        }),
        "effective-bom",
      ),
  });
}

export function useEffectiveRouting(productId: string, facilityId: string) {
  return useQuery({
    ...REF_OPTIONS,
    queryKey: productionKeys.effectiveRouting(productId, facilityId),
    enabled: productId.length > 0 && facilityId.length > 0,
    queryFn: async ({ signal }) =>
      unwrap(
        await client.GET("/v1/catalogue/products/{id}/routings/effective", {
          signal,
          params: { path: { id: productId }, query: { facility_id: facilityId } },
        }),
        "effective-routing",
      ),
  });
}

/**
 * The pinned BOM with its component lines — S3's release-step ATP panel walks
 * `bom.lines`.
 *
 * NOTE — route verified against the tree, correcting the build brief: there is
 * NO `GET /v1/catalogue/boms/{id}/lines` (that path is PUT-only, the draft
 * line-set replace); the lines are served on the BOM detail,
 * `GET /v1/catalogue/boms/{id}` (spec 01 §5 — "Full BOM (lines, outputs,
 * substitutes)"). Without `?explode` the response is the flat `Bom`.
 */
export function useBom(bomId: string) {
  return useQuery({
    ...REF_OPTIONS,
    queryKey: productionKeys.bomLines(bomId),
    enabled: bomId.length > 0,
    queryFn: async ({ signal }) =>
      unwrap(
        await client.GET("/v1/catalogue/boms/{id}", { signal, params: { path: { id: bomId } } }),
        "bom",
      ),
  });
}

/**
 * The tenant's UoM catalog (module 01 §5, `catalogue.read` — held by every
 * office role). S3 needs it to turn a product's `base_uom_id` into the
 * lowercase CODE the create body carries (`uom` is a code on the §5 row 1 wire,
 * not an id) and to offer the code select. Bounded and near-static: one cached
 * fetch.
 */
export function useUoms() {
  return useQuery({
    ...REF_OPTIONS,
    queryKey: productionKeys.uoms(),
    queryFn: async ({ signal }) =>
      unwrap(await client.GET("/v1/catalogue/uoms", { signal }), "uoms"),
  });
}

/**
 * ATP per component for the release panel (module 06 §5). ADVISORY ONLY: the
 * release response's `shortages[]` is the authoritative statement — the panel
 * paints green/amber ahead of the post, it never decides. "Earliest PO due"
 * needs module 10, which does not exist: the panel shows shortfall qty with a
 * degraded note naming procurement, never a guessed date.
 */
export function useComponentAtp(productId: string, facilityId: string) {
  return useQuery({
    retry: false,
    queryKey: productionKeys.atp(productId, facilityId),
    enabled: productId.length > 0 && facilityId.length > 0,
    queryFn: async ({ signal }) =>
      unwrap(
        await client.GET("/v1/inventory/atp", {
          signal,
          params: { query: { product_id: productId, facility_id: facilityId } },
        }),
        "atp",
      ),
  });
}

// =============================================================================
// §4 S7 — sync conflict review (§5 row 25) · slice 04-B-3
// =============================================================================

const CONFLICTS = ["production", "conflicts"] as const;

export function useFloorConflicts(query: FloorConflictsQuery = {}) {
  return useQuery({
    queryKey: productionKeys.conflicts(query),
    retry: false,
    queryFn: async ({ signal }) =>
      unwrap(
        await client.GET("/v1/floor/conflicts", { signal, params: { query } }),
        "floor-conflicts",
      ),
  });
}

/**
 * The S7 nav badge (§4 S7 "badge count in the nav"). §5 serves no count
 * endpoint, so the badge is an honest PROBE: one page of open rows, shown as
 * its exact length while `next_cursor` is null and as "N+" once the page
 * overflows — never a client-side walk of the whole queue just to draw a
 * number.
 */
export const CONFLICT_BADGE_PAGE = 50;

export function useOpenConflictBadge(enabled: boolean) {
  return useQuery({
    queryKey: productionKeys.conflictBadge(),
    retry: false,
    enabled,
    refetchInterval: 60_000,
    queryFn: async ({ signal }) =>
      unwrap(
        await client.GET("/v1/floor/conflicts", {
          signal,
          params: { query: { status: ["open"], limit: CONFLICT_BADGE_PAGE } },
        }),
        "floor-conflict-badge",
      ),
  });
}

/** What a review flag's correction did NOT change on a COMPLETED order (D-299
 * owner ruling R1): the receipt taken at completion. Generated from
 * `FloorCorrectionOutputReceiptUnchanged` (packages/contracts
 * production/floor-events.ts); `NonNullable` because the contract registers
 * the NULLABLE form as the component (`correction_effect` is null for every
 * other resolution), and the notice only ever holds a present one. */
export type FloorCorrectionEffect = NonNullable<S["FloorCorrectionOutputReceiptUnchanged"]>;

/**
 * §5 e25's `correction_effect`, READ TOLERANTLY (D-299 owner ruling R1): the
 * object when the resolution was a correction on a completed order, and
 * `undefined` otherwise — for `null` (every other resolution), and for a
 * response from a server that predates the field, where the key is ABSENT and
 * the notice simply cannot be shown. The shape is checked on the fields the
 * S7 notice prints, so a malformed object renders nothing rather than
 * "undefined m".
 */
export function correctionEffectOf(response: unknown): FloorCorrectionEffect | undefined {
  if (typeof response !== "object" || response === null) return undefined;
  const effect = (response as { correction_effect?: unknown }).correction_effect;
  if (typeof effect !== "object" || effect === null) return undefined;
  const e = effect as Record<string, unknown>;
  const printed = [
    "work_order_id",
    "receipted_qty_good",
    "corrected_operation_qty_good",
    "corrected_operation_qty_scrap",
    "uom",
  ];
  return e["output_receipt_unchanged"] === true &&
    printed.every((key) => typeof e[key] === "string" && e[key].length > 0)
    ? (effect as FloorCorrectionEffect)
    : undefined;
}

export function useResolveConflict() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; body: FloorConflictResolveRequest }) =>
      unwrap(
        await client.POST("/v1/floor/conflicts/{id}/resolve", {
          params: { path: { id: input.id }, header: idempotentHeader() },
          body: input.body,
        }),
        "resolve-conflict",
      ),
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: CONFLICTS });
      // apply_with_edit composes a NEW event through the apply pipeline — the
      // WO projections may have moved (§2.17), so the WO surfaces refresh.
      void qc.invalidateQueries({ queryKey: WORK_ORDERS });
      void qc.invalidateQueries({ queryKey: STATION_QUEUES });
      // …including the order's own DETAIL, which the list prefix does not
      // reach (`work-order` vs `work-orders`): S7's R1 notice links there to
      // show the corrected step, and a cached detail would show the old one.
      if (data.conflict.work_order_id !== null) {
        void qc.invalidateQueries({
          queryKey: productionKeys.workOrder(data.conflict.work_order_id),
        });
      }
    },
    // A SECOND RESOLVER (D-299 round 9): the first resolution committed while
    // this reader looked at the row, and e25 answered 409
    // FLOOR_CONFLICT_ALREADY_RESOLVED. The queue this reader holds is stale,
    // so it refetches, and the order surfaces follow, since the first
    // resolution may have applied a correction to them. The conflicts prefix
    // covers the nav badge's probe as well.
    onError: (error) => {
      if (error instanceof RequestError && error.code === "FLOOR_CONFLICT_ALREADY_RESOLVED") {
        void qc.invalidateQueries({ queryKey: CONFLICTS });
        void qc.invalidateQueries({ queryKey: WORK_ORDERS });
        void qc.invalidateQueries({ queryKey: ["production", "work-order"] });
      }
    },
  });
}

// =============================================================================
// §4 S8 — floor device registry (§5 row 26) · slice 04-B-3
// =============================================================================

const DEVICES = ["production", "devices"] as const;

export function useFloorDevices(query: FloorDevicesQuery = {}) {
  return useQuery({
    queryKey: productionKeys.devices(query),
    retry: false,
    queryFn: async ({ signal }) =>
      unwrap(await client.GET("/v1/floor/devices", { signal, params: { query } }), "floor-devices"),
  });
}

export function useCreateFloorDevice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: FloorDeviceCreateRequest) =>
      unwrap(
        await client.POST("/v1/floor/devices", { params: { header: idempotentHeader() }, body }),
        "create-floor-device",
      ),
    onSuccess: () => void qc.invalidateQueries({ queryKey: DEVICES }),
  });
}

export function useUpdateFloorDevice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; body: FloorDeviceUpdateRequest }) =>
      unwrap(
        await client.PATCH("/v1/floor/devices/{id}", {
          params: { path: { id: input.id }, header: idempotentHeader() },
          body: input.body,
        }),
        "update-floor-device",
      ),
    onSuccess: () => void qc.invalidateQueries({ queryKey: DEVICES }),
  });
}

/**
 * Scrap reasons for the record-result form (module 05's cause catalog).
 *
 * NOTE — route verified against the tree, correcting the build brief: there is
 * NO `GET /v1/waste/causes` (that path is POST-only in openapi/v1.json); the
 * cause catalog is served by `GET /v1/waste/categories?include=causes` (spec 05
 * §5 row 1, open to any authenticated principal), exactly as waste-queries.ts
 * reads it.
 */
export function useScrapReasons(enabled = true) {
  return useQuery({
    ...REF_OPTIONS,
    enabled,
    queryKey: productionKeys.scrapReasons(),
    queryFn: async ({ signal }) => {
      const res = await client.GET("/v1/waste/categories", {
        signal,
        params: { query: { include: "causes" } },
      });
      const data = unwrap(res, "scrap-reasons");
      return data.causes ?? [];
    },
  });
}

/**
 * The §2.6 downtime-reason catalog (spec 04 §5 row 23, `production.read` — the
 * same gate as every screen that renders a reason). Fetched WITHOUT the
 * `is_active` filter on purpose: the Events tab reads history, and a
 * deactivated reason still names the pause it once explained — `active: false`
 * hides a reason from the floor grid, never from the record.
 *
 * One vocabulary rule (review finding, 2026-08-08): S10/S11 render a reason by
 * its `name_i18n`, so the office timeline must too — the same physical fact
 * must never read as a raw `DIE_CHANGE` on one surface and «Смена фильеры» on
 * another.
 */
export function useDowntimeReasons() {
  return useQuery({
    ...REF_OPTIONS,
    queryKey: productionKeys.downtimeReasons(),
    queryFn: async ({ signal }) => {
      const res = await client.GET("/v1/downtime/reasons", { signal });
      return unwrap(res, "downtime-reasons").items;
    },
  });
}

// =============================================================================
// §4 S13 — the blocked-stations board (§5 e22, `open_only`)
// =============================================================================

/** One §2.7 `downtime_logs` row as e22 serves it. NO reason label and NO
 * station name: the row carries `downtime_reason_id` / `work_center_id` and the
 * client joins the two catalogs it already caches — the contract's own stated
 * intent ("the label resolves client-side … so the row survives a reason
 * rename", packages/contracts/src/production/floor-events.ts). */
export type DowntimeLog = S["DowntimeLog"];
export type DowntimeReason = S["DowntimeReason"];

/**
 * §5 e22 with `open_only=true` — every downtime interval that is still running.
 *
 * THE FILTER IS THE WHOLE QUESTION, so it is not optional here: this hook only
 * ever asks for OPEN rows. A closed interval is history and belongs on the
 * scoreboard's minute split (§3.7), not on a board a planner reads at 07:00 to
 * decide where to walk.
 *
 * `refetchInterval` is the §9.16 sixty-second fallback the scoreboard already
 * takes, and for the same reason: this screen must be right within a minute
 * even when nothing pushes to it. The board also re-renders its elapsed column
 * on its own tick, which is a DIFFERENT clock — one keeps the ROWS fresh, the
 * other keeps the DURATIONS honest, and a row that stops accruing while the
 * page is open would be the more dangerous of the two lies.
 *
 * EVERY OPEN STOPPAGE IS READ, NOT THE FIRST PAGE (D-299 round 3, "S13
 * paging"). This hook used to read ONE page of 200 and tell the reader when
 * more existed. That was an honest note and the wrong read, because since
 * §5 e35 this board is the only surface that can END a stoppage from the
 * office, and e22 serves NEWEST first (`ORDER BY d.id DESC`): the rows that
 * hold a finished order's §3.1 close are by construction the OLD op-bound
 * ones opened while the order was live, so they were the first to fall off
 * the only screen that could release them (round-2 refutation). The queryFn
 * now follows `next_cursor` to the end — open stoppages are a bounded set,
 * one per stopped machine — so the board, its per-station grouping and its
 * "longest stop first" order are computed over the whole set.
 *
 * The follow is BOUNDED, because an unbounded loop against a server that
 * kept answering a cursor would be a hung board: after
 * `OPEN_DOWNTIME_MAX_PAGES` pages the last cursor is returned as
 * `next_cursor`, and the screen says the list is incomplete and names the
 * number it read (`OPEN_DOWNTIME_READ_CAP`). A plant with that many machines
 * stopped at once has a different problem than this screen.
 */
export const OPEN_DOWNTIME_PAGE = 200;
/** Pages the open-downtime read follows before it stops and says so. */
export const OPEN_DOWNTIME_MAX_PAGES = 50;
/** The most open stoppages one read returns — the number the board's
 * incomplete-list sentence names. */
export const OPEN_DOWNTIME_READ_CAP = OPEN_DOWNTIME_PAGE * OPEN_DOWNTIME_MAX_PAGES;

export function useOpenDowntime(facilityId: string | undefined, enabled: boolean) {
  const facility = facilityId !== undefined && facilityId.length > 0 ? facilityId : undefined;
  return useQuery({
    queryKey: productionKeys.downtimeOpen(facility),
    retry: false,
    enabled,
    refetchInterval: 60_000,
    queryFn: async ({ signal }): Promise<S["DowntimeLogListResponse"]> => {
      const items: DowntimeLog[] = [];
      let cursor: string | undefined;
      for (let page = 0; page < OPEN_DOWNTIME_MAX_PAGES; page += 1) {
        const served = unwrap(
          await client.GET("/v1/downtime", {
            signal,
            params: {
              query: {
                open_only: "true",
                limit: OPEN_DOWNTIME_PAGE,
                ...(facility !== undefined ? { facility_id: facility } : {}),
                ...(cursor !== undefined ? { cursor } : {}),
              },
            },
          }),
          "downtime-open",
        );
        items.push(...served.items);
        if (served.next_cursor === null) return { items, next_cursor: null };
        cursor = served.next_cursor;
      }
      // Stopped at the bound with the server still holding more: the cursor
      // rides out so the board says the list is incomplete (never silently).
      return { items, next_cursor: cursor ?? null };
    },
  });
}

/** §5 e35's answer: the ended row, and the work order whose §3.1 close it was
 * holding (`null` for station downtime, which never holds one). */
export type DowntimeLogEndResponse = S["DowntimeLogEndResponse"];

/** The open-downtime cache prefix every facility's e22 page lives under. */
const DOWNTIME_OPEN = ["production", "downtime", "open"] as const;

/**
 * §5 e35 — END AN OPEN STOPPAGE FROM THE OFFICE (added 2026-09-25, D-299 owner
 * ruling H). The ender §3.1's close guard lacked: an op-bound row opened while
 * the order was live blocks its close after completion, and the floor composes
 * `downtime_ended` only from a station still showing a live operation, which a
 * finished order's station often does not. Gated server-side on
 * `production.conflict.resolve` (e25's code — ending a stoppage the floor never
 * ended is the conflict queue's own act).
 *
 * THE KEY RIDES THE INTENT, NOT THE CALL. The caller mints one
 * `Idempotency-Key` when the confirm opens and passes it on every retry of
 * that same end, so a lost response replays the first 200 instead of meeting
 * its own 409. `endedAt` is an ISO instant with offset or absent (= now).
 *
 * ON SUCCESS THE ROW LEAVES THE OPEN LIST AT ONCE — removed from every cached
 * e22 page before the refetch, because a stoppage the office just ended must
 * not sit on the board for up to a minute looking still open — and the work
 * order, the station queues and the scoreboard (§9.11 recomputes the closed
 * interval) are re-read. ON `DOWNTIME_ALREADY_ENDED` (409: an earlier call,
 * another admin, or the floor's own end won the row) the open list is re-read
 * too, so the stale row goes the same way.
 */
export function useEndDowntime() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      note: string;
      endedAt?: string | undefined;
      idempotencyKey: string;
    }) =>
      unwrap(
        await client.POST("/v1/downtime/{id}/end", {
          params: {
            path: { id: input.id },
            header: { "Idempotency-Key": input.idempotencyKey },
          },
          body: {
            note: input.note,
            ...(input.endedAt !== undefined ? { ended_at: input.endedAt } : {}),
          },
        }),
        "end-downtime",
      ),
    onSuccess: (data, input) => {
      qc.setQueriesData<S["DowntimeLogListResponse"]>({ queryKey: DOWNTIME_OPEN }, (page) =>
        page === undefined
          ? page
          : { ...page, items: page.items.filter((log) => log.id !== input.id) },
      );
      void qc.invalidateQueries({ queryKey: DOWNTIME_OPEN });
      void qc.invalidateQueries({ queryKey: WORK_ORDERS });
      if (data.work_order_id !== null) {
        void qc.invalidateQueries({ queryKey: productionKeys.workOrder(data.work_order_id) });
      }
      void qc.invalidateQueries({ queryKey: STATION_QUEUES });
      void qc.invalidateQueries({ queryKey: ["production", "scoreboard"] });
    },
    onError: (error) => {
      if (error instanceof RequestError && error.code === "DOWNTIME_ALREADY_ENDED") {
        void qc.invalidateQueries({ queryKey: DOWNTIME_OPEN });
      }
    },
  });
}

/** The result of probing ONE station's queue — the three outcomes a board has
 * to tell apart. `absent` is a station the caller may not read (§7's e14 read
 * fence answers 404 identically to "does not exist"), which is neither "no
 * queue" nor "an error": it must be excluded from the checked count rather than
 * counted as clear. */
export interface StationQueueProbe {
  readonly workCenterId: string;
  readonly operations: readonly StationQueueOperation[];
  readonly isPending: boolean;
  readonly isAbsent: boolean;
  readonly isError: boolean;
}

/**
 * The station queues of a SET of work centres, one query each.
 *
 * SAME KEYS AS THE DISPATCH BOARD (`productionKeys.stationQueue`), deliberately:
 * S4 already fetches exactly these, so moving between the two screens costs
 * nothing and a priority write invalidates both in one call — the D-072 rule
 * that the SERVER re-orders and the client only re-fetches holds across both.
 *
 * WHY A FAN-OUT AND NOT ONE CALL. §5 has no endpoint that answers "which
 * operations are on quality hold" across a plant: `qc_hold` is a column on
 * `work_order_operations` and the only read that serves it is e14, which is
 * keyed by work centre. The alternative — leaving the quality half off the
 * board — would ship a blocked-stations screen that cannot see the second of
 * the two things that block a station, and a planner would learn that only by
 * being wrong. So the fan-out is BOUNDED BY CONSTRUCTION instead: the caller
 * passes the ACTIVE stations of ONE facility, and the screen states how many it
 * checked, so "nothing is blocked" is a claim with a scope attached rather than
 * an assumption.
 */
export function useStationQueues(
  workCenterIds: readonly string[],
  enabled: boolean,
): StationQueueProbe[] {
  return useQueries({
    queries: workCenterIds.map((workCenterId) => ({
      queryKey: productionKeys.stationQueue(workCenterId),
      retry: false,
      enabled: enabled && workCenterId.length > 0,
      refetchInterval: 60_000,
      queryFn: async ({ signal }: { signal: AbortSignal }) =>
        unwrap(
          await client.GET("/v1/floor/stations/{workCenterId}/queue", {
            signal,
            params: { path: { workCenterId } },
          }),
          "station-queue",
        ),
    })),
    combine: (results) =>
      results.map((result, index) => {
        const workCenterId = workCenterIds[index] ?? "";
        const absent = result.error instanceof RequestError && result.error.httpStatus === 404;
        return {
          workCenterId,
          operations: result.data?.operations ?? [],
          isPending: result.isPending,
          isAbsent: absent,
          isError: result.error != null && !absent,
        };
      }),
  });
}

// =============================================================================
// §4 S6 + the S2 Calibration tab — slice 04-C-2 (§5 e20/e21)
// =============================================================================

export type CalibrationRecord = S["CalibrationRecord"];
export type CalibrationDrift = S["CalibrationDriftResponse"];
export type CalibrationRecordsQuery = NonNullable<
  paths["/v1/calibration/records"]["get"]["parameters"]["query"]
>;

/** §5 e20 — the drift series for a work centre (buckets + recent records +
 * the trailing-window status computed by the SAME kernel decision the weekly
 * cron runs, so the chart can never disagree with it). */
export function useCalibrationDrift(workCenterId: string | undefined, bucket: "day" | "week") {
  return useQuery({
    queryKey: ["production", "calibration", "drift", workCenterId ?? null, bucket] as const,
    retry: false,
    enabled: workCenterId !== undefined && workCenterId.length > 0,
    queryFn: async ({ signal }) =>
      unwrap(
        await client.GET("/v1/calibration/work-centers/{workCenterId}/drift", {
          signal,
          params: { path: { workCenterId: workCenterId ?? "" }, query: { bucket } },
        }),
        "calibration-drift",
      ),
  });
}

/** §5 e21 — the barrel records list (the S2 Calibration tab passes
 * `work_order_id`; S6's recent-barrels table rides the e20 payload instead). */
export function useCalibrationRecords(query: CalibrationRecordsQuery, enabled: boolean) {
  return useQuery({
    queryKey: ["production", "calibration", "records", query] as const,
    retry: false,
    enabled,
    queryFn: async ({ signal }) =>
      unwrap(
        await client.GET("/v1/calibration/records", { signal, params: { query } }),
        "calibration-records",
      ),
  });
}

// ---- §5 e17 / e27 — the scoreboard and the piece-rate list (slice 04-C-4) ---

/** One §2.10 row as e17 serves it. NO money — §2.10 gives the scoreboard no
 * cost columns, because it hangs on a wall in a corridor. */
export type ShiftScoreboardRow = S["ShiftScoreboardRow"];
export type ShiftScoreboardFacilityTotal = S["ShiftScoreboardFacilityTotal"];
export type ShiftScoreboard = S["ShiftScoreboardResponse"];
export type PieceRateEntry = S["PieceRateEntry"];

export interface ScoreboardQuery {
  shift_date: string;
  facility_id?: string;
  shift_definition_id?: string;
  work_center_id?: string;
}

/**
 * §5 e17. `refetchInterval` is §9.16's 60-second POLLING FALLBACK, and it is
 * unconditional on purpose: the SSE stream is a latency upgrade, never a
 * correctness dependency, so the screen must be right (within a minute) even
 * when the socket is gone and nothing told it so.
 */
export function useShiftScoreboard(query: ScoreboardQuery, enabled = true) {
  return useQuery({
    queryKey: ["production", "scoreboard", query] as const,
    retry: false,
    enabled: enabled && query.shift_date.length > 0,
    refetchInterval: 60_000,
    queryFn: async ({ signal }) =>
      unwrap(
        await client.GET("/v1/shifts/scoreboard", { signal, params: { query } }),
        "shift-scoreboard",
      ),
  });
}

/** §2.13's closed status vocabulary, taken OFF the row rather than off a
 * component alias — the enum is inlined into the row, so deriving it here is
 * what keeps the screen and the wire from being able to disagree. */
export type PieceRateStatus = PieceRateEntry["status"];

/** §5 e27's filter set, as the endpoint serves it. `worker_id` is honoured only
 * for a caller holding `production.view_costs`; everyone else is pinned to
 * their own rows service-side whatever this asks for (the D-040 two-layer
 * pattern) — so a screen may send it and must never claim it was applied. */
export interface PieceRateQuery {
  worker_id?: string;
  facility_id?: string;
  status?: PieceRateStatus;
  shift_date_from?: string;
  shift_date_to?: string;
  cursor?: string;
  limit?: number;
}

/** §5 e27. `rate_minor`/`amount_minor` are ABSENT for a principal outside
 * §2.13's visibility list (§7's redaction shape) and NULL for everyone else
 * until §12 Q1's rate table ships — so a screen branches on `key !== undefined`
 * and then on `!== null`, and renders neither as 0. */
export function usePieceRateEntries(query: PieceRateQuery, enabled = true) {
  return useQuery({
    queryKey: ["production", "piece-rate", query] as const,
    retry: false,
    enabled,
    queryFn: async ({ signal }) =>
      unwrap(
        await client.GET("/v1/piece-rate/entries", { signal, params: { query } }),
        "piece-rate-entries",
      ),
  });
}

/**
 * Worker id → display name for S14's ledger (§4 row 14).
 *
 * ONE list read, not one per id, and ONLY when the caller may make it: `GET
 * /v1/members` is gated on `identity.view_members`, which of S14's audience
 * only group_admin and facility_admin hold — finance, the role the piece-rate
 * capture exists for, does not. Firing a call we know will 403 is the dead call
 * D-088 ruling 10d forbids (three 403s per page load, a red panel and a Retry
 * that can never succeed), so `enabled` carries the role test and the rows fall
 * back to the short id in a `<code>` chip through `RefLabel` — an identifier,
 * never a broken name. Module 06's `usePrincipalIndex` is the same hook for the
 * same reason; each area owns its own because the query keys and the cache
 * lifetimes are the area's.
 */
export function useWorkerIndex(enabled: boolean) {
  const query = useQuery({
    ...REF_OPTIONS,
    queryKey: ["production", "ref", "members"] as const,
    enabled,
    queryFn: async ({ signal }) =>
      unwrap(
        await client.GET("/v1/members", { signal, params: { query: { limit: 200 } } }),
        "members",
      ),
  });
  const byId = new Map<string, string>();
  for (const member of query.data?.items ?? []) byId.set(member.user_id, member.display_name);
  return { byId, isPending: enabled && query.isPending, enabled };
}

// ===========================================================================
// §2.11 defect hand-offs — §5 e18/e19, and §5 e32's quality-hold release
// (slice 04-C-5)
// ===========================================================================

/** One §2.11 row as e18 serves it. NO money — a hand-off carries quantities,
 * reason codes and two shift attributions; the scrap it may become is costed on
 * the WORK ORDER's ledger, behind `production.view_costs`, elsewhere. */
export type DefectHandoff = S["DefectHandoff"];
/** §2.11's four values, taken OFF the row rather than off a component alias:
 * the enum is not a named OpenAPI component (it is inlined into both the row
 * and the request), so deriving it from the row is what keeps the client and
 * the wire from being able to disagree. */
export type DefectHandoffDisposition = DefectHandoff["disposition"];

const DEFECT_HANDOFFS = ["production", "defect-handoffs"] as const;

export interface DefectHandoffQuery {
  disposition?: DefectHandoffDisposition;
  /** EITHER side — a shift's own ledger is both what it handed off and what it
   * flagged, and asking a supervisor to run two queries and merge them is
   * asking them not to. */
  shift_instance_id?: string;
  produced_shift_instance_id?: string;
  work_order_id?: string;
  facility_id?: string;
  limit?: number;
}

/**
 * §5 e18. Polled on the same 60-second fallback cadence as the scoreboard it
 * sits under (§9.16): the panel and the board describe the same shift, and a
 * ledger a minute out of step with the numbers above it invites the reader to
 * trust the wrong one.
 */
export function useDefectHandoffs(query: DefectHandoffQuery, enabled = true) {
  return useQuery({
    queryKey: [...DEFECT_HANDOFFS, query] as const,
    retry: false,
    enabled,
    refetchInterval: 60_000,
    queryFn: async ({ signal }) =>
      unwrap(
        await client.GET("/v1/defect-handoffs", { signal, params: { query } }),
        "defect-handoffs",
      ),
  });
}

/**
 * §5 e19 — THE DECISION. It moves a number onto the PRODUCING shift's
 * scoreboard row (§3.7), so the scoreboard is invalidated alongside the ledger:
 * a panel that showed the disposition and left the board above it stale would
 * be showing a supervisor two contradictory answers on one screen, which is the
 * one thing an attribution surface must not do.
 *
 * The work-order detail and the station queues are invalidated too, because
 * `rework` re-opens an operation (§3.2) — the step goes back to `in_progress`
 * and reappears in its station's queue.
 */
export function useDisposeDefectHandoff() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      disposition: Exclude<DefectHandoffDisposition, "pending">;
      note?: string;
    }) =>
      unwrap(
        await client.POST("/v1/defect-handoffs/{id}/disposition", {
          params: { path: { id: input.id }, header: idempotentHeader() },
          body: {
            disposition: input.disposition,
            ...(input.note !== undefined && input.note !== "" ? { note: input.note } : {}),
          },
        }),
        "dispose-defect-handoff",
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: DEFECT_HANDOFFS });
      void qc.invalidateQueries({ queryKey: ["production", "scoreboard"] });
      void qc.invalidateQueries({ queryKey: WORK_ORDERS });
      void qc.invalidateQueries({ queryKey: STATION_QUEUES });
    },
  });
}

/**
 * §5 e32 — the manual `qc_hold` release, note REQUIRED. Nothing in this release
 * can SET the hold (module 07 is its only writer — D-071), so the control is
 * rendered only on an operation that IS held, which in this deployment is none:
 * the button is present in the product and invisible on every current screen,
 * which is the honest state rather than a disabled shell.
 */
export function useReleaseQcHold() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { workOrderId: string; opId: string; note: string }) =>
      unwrap(
        await client.POST("/v1/work-orders/{id}/operations/{opId}/qc-hold/release", {
          params: {
            path: { id: input.workOrderId, opId: input.opId },
            header: idempotentHeader(),
          },
          body: { note: input.note },
        }),
        "release-qc-hold",
      ),
    onSuccess: (_data, input) => {
      void qc.invalidateQueries({ queryKey: productionKeys.workOrder(input.workOrderId) });
      void qc.invalidateQueries({ queryKey: WORK_ORDERS });
      void qc.invalidateQueries({ queryKey: STATION_QUEUES });
    },
  });
}
